// Jira descriptions in this data are a hybrid: people type Markdown for emphasis
// (**bold** appears in 1865 of 2334 descriptions) but lists, links and code blocks are
// stored as Jira wiki markup. The shared renderMarkdown() util only speaks Markdown, so
// the wiki-only constructs are translated into the Markdown subset it understands before
// it runs. renderMarkdown() itself is left untouched — it is shared with the campaign
// overview and the admin editor.
//
// The important one is `#` at line start: in Jira that is an ORDERED LIST ITEM, not an
// H1. Rendered as Markdown, every reproduction step becomes a giant heading.

// A lone `# Something` is more likely a genuine Markdown heading than a one-item list,
// so `#` is only treated as a list when the text has at least this many such lines.
const MIN_HASH_LINES_FOR_LIST = 2;

// Sentinel for parked code regions. Deliberately not a control character, and unlikely
// to occur in ticket text.
const PARK_PREFIX = '@@JIRAMD-CODE-';
const PARK_SUFFIX = '@@';

function isSafeUrl(url) {
  return /^(https?:\/\/|\/|#)/i.test(url.trim());
}

// Jira wraps inserted/subscript text in +text+ / ~text~ — strip those markers inside
// link labels, where they are pure noise.
function cleanLabel(label) {
  return label.replace(/[+~]([^+~]+)[+~]/g, '$1').trim();
}

export function normaliseJiraMarkup(input) {
  if (!input) return input;

  // Pull code regions out first so their contents are not rewritten, then restore them.
  const stash = [];
  const park = (text) => {
    stash.push(text);
    return `${PARK_PREFIX}${stash.length - 1}${PARK_SUFFIX}`;
  };

  let out = input
    // ```fenced``` — already Markdown, just protect it
    .replace(/```[\s\S]*?```/g, (m) => park(m))
    // {code:java}...{code} and {noformat}...{noformat}
    .replace(
      /\{(?:code|noformat)(?::[^}]*)?\}([\s\S]*?)\{(?:code|noformat)\}/g,
      (_, body) => park('```\n' + body.trim() + '\n```')
    );

  // {{monospace}} → `monospace`
  out = out.replace(/\{\{([^}\n]+)\}\}/g, '`$1`');

  // Jira headings: "h2. Title" → "## Title"
  out = out.replace(/^h([1-6])\.\s+(.+)$/gm, (_, level, text) => `${'#'.repeat(Number(level))} ${text}`);

  // [label|target] → [label](target). Jira also writes [url|url].
  // Targets that are not http(s)/relative are dropped to plain text — these descriptions
  // are user-authored and get injected as HTML downstream.
  out = out.replace(/\[([^\]|\n]*)\|([^\]\n]+)\]/g, (whole, label, target) => {
    const url = target.trim();
    const raw = label.trim();
    // A label that is itself a URL is left alone: `+` in a URL is an encoded space, not
    // Jira's inserted-text marker, and cleanLabel would eat it.
    const text = (isSafeUrl(raw) ? raw : cleanLabel(raw)) || url;
    if (!isSafeUrl(url)) return text;
    return `[${text}](${url})`;
  });

  // Ordered lists: leading #, ##, ### are list depth in Jira, not heading level.
  const hashLines = out.match(/^#+\s+\S/gm) ?? [];
  if (hashLines.length >= MIN_HASH_LINES_FOR_LIST) {
    // renderMarkdown only recognises flat `- ` items, so depth is flattened.
    out = out.replace(/^#+\s+(.+)$/gm, '- $1');
  }

  // Remaining +inserted+ markers outside links
  out = out.replace(/(^|\s)\+([^+\n]+)\+(?=\s|$)/gm, '$1$2');

  return out.replace(
    new RegExp(`${PARK_PREFIX}(\\d+)${PARK_SUFFIX}`, 'g'),
    (_, i) => stash[Number(i)]
  );
}
